// @flow
import format from 'date-fns/fp/formatWithOptions';
import it from 'date-fns/locale/it';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import de from 'date-fns/locale/de';
import ko from 'date-fns/locale/ko';
import ja from 'date-fns/locale/ja';
// eslint-disable-next-line camelcase
import en_GB from 'date-fns/locale/en-GB';
// eslint-disable-next-line camelcase
import zh from 'date-fns/locale/zh-TW';
import mapValues from 'lodash/fp/mapValues';

import { toDate } from './date';

/**
 * Take a locale in kebab-case like 'en-us' and transform
 * into expected format for the currency formatter (like 'en_US')
 *
 * 💁 This isn't exactly kebab-case and snake_case, since when changing to
 * snake format, we want to capitalize the country code also. (That's what
 * currencyFormatter.js is expecting.)
 *
 */
export const convertLocale = (
  locale: string,
  codeFormat: 'kebab' | 'snake',
): string => {
  if (codeFormat === 'kebab') {
    return locale.replace(/_/g, '-');
  }
  return locale
    .replace(/-/g, '_')
    .replace(/_(.*)/, (match) => match.toUpperCase());
};

// locales imported from date-fns provide translations for Weekdays etc. for the respective language
const dateFnsLocales = {
  de,
  it,
  es,
  fr,
  zh,
  ko,
  ja,
  en_GB,
};

// get language portion of the ISO/IEC 15897 locale code
export const isoLanguageCode = (locale: string): string => {
  const languageCode = convertLocale(locale, 'snake').split('_')[0];
  if (languageCode === 'undefined') return 'en';
  return languageCode;
};

// get territory portion of the ISO/IEC 15897 locale code
export const isoTerritoryCode = (locale: string): string => {
  const territoryCode = convertLocale(locale, 'snake').split('_')[1];
  if (territoryCode === 'undefined') return '';
  return territoryCode;
};

export const fnsLocalesHasLanguageCode = (languageCode: string): boolean =>
  // $FlowFixMe[method-unbinding]
  Object.prototype.hasOwnProperty.call(dateFnsLocales, languageCode);

// return a format compatible with the datefns locale format
// which atm only cover translations based on the languageCode and
// lack especifications on the territoryCode.
// Expected format passed in from scramble4: en_US, fr_FR etc.
// In case of GB we need to keep the territoryCode to account for the
// starting of day on Monday vs Sunday for en_US
export const dateFnsLocale = (locale: string): string => {
  const fnsLanguageCode = isoLanguageCode(locale);
  const territoryCode = isoTerritoryCode(locale);
  if (territoryCode === 'GB') return locale;
  if (!fnsLocalesHasLanguageCode(fnsLanguageCode)) {
    return 'en';
  }
  return fnsLanguageCode;
};

type localeFnsData = {
  de: string[],
  it: string[],
  es: string[],
  fr: string[],
  en_GB: string[],
  zh: string[],
  ko: string[],
  ja: string[],
};

// create MONTHS object using date-fns localization data
export const MONTHS: localeFnsData = mapValues((locale) =>
  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    .map((month) => toDate(`2019-${month.toString().padStart(2, '0')}-15`))
    .map((date) => format({ locale })('MMMM')(date)),
)(dateFnsLocales);

// create WEEKDAYS object using date-fns localization data
export const WEEKDAYS: localeFnsData = mapValues((locale) =>
  // the dates 6 to 12 of January 2019 are the days of the week Sunday to Saturday
  [6, 7, 8, 9, 10, 11, 12]
    .map((day) => toDate(`2019-01-${day.toString().padStart(2, '0')}`))
    .map((date) => format({ locale })('ddd')(date)),
)(dateFnsLocales);

// The first day of calendar should be Monday (1) for European countries
// and Sunday(0) for US, South America and China
export const FIRST_DAY_OF_WEEK = {
  en: 0,
  zh: 0,
  ja: 0,
  ko: 0,
  en_GB: 1,
  it: 1,
  es: 1,
  fr: 1,
  de: 1,
};
